import { useState, useCallback, useEffect } from 'react';
import type { CrumbItem } from 'contexts/BreadcrumbsProvider';

export const useExtraCrumbs = () => {
  const [extras, setExtras] = useState<Record<string, CrumbItem>>({});
  const [removedCrumbs, setRemovedCrumbs] = useState<CrumbItem[]>([]);
  const [itemToRestore, setItemToRestore] = useState<CrumbItem | null>(null);
  const [clickedBreadcrumb, setClickedBreadcrumb] = useState<boolean>(false);
  const [clickedBreadcrumbsAndThenBack, setClickedBreadcrumbsAndThenBack] = useState<boolean>(false);

  useEffect(() => {
    if (itemToRestore) {
      const { id, label, path } = itemToRestore ?? {};
      setExtras((prev) => ({
        ...prev,
        [id]: { id, label, path },
      }));
      setItemToRestore(null);
    }
  }, [itemToRestore]);

  const insert = useCallback((crumb: CrumbItem) => {
    setExtras((prev) => ({ ...prev, [crumb.id]: crumb }));
  }, []);

  const remove = useCallback((ids: string[]) => {
    setExtras((prev) => {
      const newExtras = { ...prev };
      ids.forEach((id) => {
        delete newExtras[id];
      });
      return newExtras;
    });
  }, []);

  const pushRemovedByIds = useCallback(
    (ids: string[]) => {
      for (const id of ids) {
        setRemovedCrumbs((prev) => {
          const crumb = extras[id];
          if (crumb) {
            const newRemoved = [...prev, crumb];
            return newRemoved;
          }
          return prev;
        });
      }
    },
    [extras]
  );

  const pushRemoved = useCallback((crumb: CrumbItem) => {
    setRemovedCrumbs((prev) => {
      const newRemoved = [...prev, crumb];
      return newRemoved;
    });
  }, []);

  const removeAllExtras = useCallback(() => {
    setRemovedCrumbs((prev) => {
      const newRemoved = [...prev, ...Object.values(extras)];
      return newRemoved;
    });
    setExtras({});
  }, [extras]);

  const clearExtras = useCallback(() => {
    setExtras({});
    setRemovedCrumbs([]);
    setClickedBreadcrumb(false);
  }, []);

  const restoreLatestExtra = useCallback(() => {
    setRemovedCrumbs((prevRemoved) => {
      if (prevRemoved.length === 0) return prevRemoved;

      const latestRemoved = prevRemoved[prevRemoved.length - 1];
      setItemToRestore(latestRemoved);

      return prevRemoved.slice(0, -1);
    });
  }, []);

  const restoreAllRemovedExtras = useCallback(() => {
    setRemovedCrumbs((currentRemovedCrumbs) => {
      setExtras((prev) => {
        const newExtras = { ...prev };
        for (const removedCrumb of currentRemovedCrumbs) {
          newExtras[removedCrumb.id] = removedCrumb;
        }
        return newExtras;
      });
      return [];
    });
  }, []);

  return {
    extras,
    removedCrumbs,
    clickedBreadcrumb,
    setClickedBreadcrumb,
    insert,
    remove,
    removeAllExtras,
    clearExtras,
    restoreLatestExtra,
    restoreAllRemovedExtras,
    pushRemoved,
    pushRemovedByIds,
    setRemovedCrumbs,
    clickedBreadcrumbsAndThenBack,
    setClickedBreadcrumbsAndThenBack,
  };
};
